import type { AdmissionCode } from "@shared/types/rejections.ts";

type SectionRequirement =
  | { kind: "section"; key: string }
  | { kind: "all"; items: SectionRequirement[] }
  | { kind: "either"; items: SectionRequirement[] }
  | { kind: "any"; items: SectionRequirement[] };

const COVERAGE_HEADLINES: Partial<Record<AdmissionCode, string>> = {
  "coverage.missing_annotation_sections": "Not enough of the paper to annotate it.",
  "coverage.missing_judgment_sections": "Not enough of the paper to judge its evidence.",
};

const TOKEN = /\s*(&&|\|\||\[\]|\[|\]|\(|\)|,|[A-Za-z_]+)/y;

function tokenize(expression: string): string[] | null {
  const tokens: string[] = [];
  TOKEN.lastIndex = 0;
  while (TOKEN.lastIndex < expression.length) {
    const match = TOKEN.exec(expression);
    if (match === null) return null;
    tokens.push(match[1]);
  }
  return tokens;
}

/** Reads the section query a coverage finding quotes, e.g. `(abstract && (methods || results)) &&
 *  [abstract, (methods || results)][]`; null when it is not one this understands. */
function parseSectionRequirement(expression: string): SectionRequirement | null {
  const tokens = tokenize(expression);
  if (tokens === null) return null;
  let position = 0;

  const expect = (token: string) => {
    if (tokens[position] !== token) throw new SyntaxError(token);
    position += 1;
  };

  const parseEither = (): SectionRequirement => {
    const items = [parseAll()];
    while (tokens[position] === "||") {
      position += 1;
      items.push(parseAll());
    }
    return items.length === 1 ? items[0] : { kind: "either", items };
  };

  const parseAll = (): SectionRequirement => {
    const items = [parseAtom()];
    while (tokens[position] === "&&") {
      position += 1;
      items.push(parseAtom());
    }
    // An "all of" query repeats its terms as a trailing selection; that is not a further need.
    const echo = items.length > 1 && items[items.length - 1].kind === "any";
    const needs = echo ? items.slice(0, -1) : items;
    return needs.length === 1 ? needs[0] : { kind: "all", items: needs };
  };

  const parseAtom = (): SectionRequirement => {
    const token = tokens[position];
    if (token === "(") {
      position += 1;
      const inner = parseEither();
      expect(")");
      return inner;
    }
    if (token === "[") {
      position += 1;
      const items = [parseEither()];
      while (tokens[position] === ",") {
        position += 1;
        items.push(parseEither());
      }
      expect("]");
      if (tokens[position] === "[]") position += 1;
      return { kind: "any", items };
    }
    if (token !== undefined && /^[A-Za-z_]+$/.test(token)) {
      position += 1;
      return { kind: "section", key: token };
    }
    throw new SyntaxError(token ?? "end");
  };

  try {
    const requirement = parseEither();
    return position === tokens.length ? requirement : null;
  } catch {
    return null;
  }
}

function sectionName(key: string): string {
  return key === "coi" ? "conflict of interest" : key.replace(/_/g, " ");
}

function listInWords(parts: string[], joiner: "and" | "or"): string {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} ${joiner} ${parts[parts.length - 1]}`;
}

function describeRequirement(requirement: SectionRequirement): string {
  switch (requirement.kind) {
    case "section":
      return sectionName(requirement.key);
    case "all":
      return listInWords(requirement.items.map(describeRequirement), "and");
    case "either":
    case "any": {
      const parts = requirement.items.map(describeRequirement);
      if (parts.length === 1) return parts[0];
      return parts.length === 2
        ? `either ${parts[0]} or ${parts[1]}`
        : `at least one of ${listInWords(parts, "or")}`;
    }
  }
}

function readList(message: string, name: string): string[] | null {
  const match = new RegExp(`${name}=\\[(.*?)\\]`).exec(message);
  if (match === null) return null;
  return match[1]
    .split(",")
    .map((item) => item.replace(/['"\s]/g, ""))
    .filter((item) => item !== "")
    .map(sectionName);
}

/**
 * A coverage rejection's message in plain words ("Needs abstract and either methods or results,
 * but found only abstract."), read from the section query and the sections it quotes. Null for a
 * message that is not in that form, which is then shown as it is.
 */
export function explainAdmissionFinding(code: AdmissionCode, message: string): string | null {
  const headline = COVERAGE_HEADLINES[code];
  if (headline === undefined) return null;

  const found = readList(message, "found");
  if (found === null) return null;
  const foundText =
    found.length === 0 ? "found no sections" : `found only ${listInWords(found, "and")}`;

  const requiredMatch = /required=(.*?), found=/.exec(message);
  if (requiredMatch !== null) {
    const requirement = parseSectionRequirement(requiredMatch[1]);
    if (requirement === null) return null;
    return `${headline} Needs ${describeRequirement(requirement)}, but ${foundText}.`;
  }

  const missing = readList(message, "missing");
  if (missing === null || missing.length === 0) return null;
  return `${headline} Missing ${listInWords(missing, "and")}, and ${foundText}.`;
}
